import router from "@/routers"
import { ProviderTypeEnum, UserTypeEnum } from "@vitable/asclepius-api-client-ts"
import eventBus from "@/events/event-bus"
import globalEvents from "@/events/global-events"
import { useRefreshAccessToken } from "@/modules/auth/composables/auth"
import asclepiusConfig from "@common/config/asclepius-config"

// initial state
const state = () => ({
  twoFactorPendingDetails: {
    emailOrPhone: undefined,
    userType: undefined
  },
  twoFactorPending: false,
  token: undefined,
  refreshToken: undefined,
  user: undefined
})

// getters
const getters = {
  allowAsyncVisitFeatures: (state: any) => {
    return (
      (!!state && !!state.user && state.user.email === "dev@vitablehealth.com") ||
      state.user.email === "caitlin@vitablehealth.com" ||
      state.user.email === "np@np.com" ||
      state.user.email === "melanie@vitablehealth.com" ||
      state.user.email === "kate@vitablehealth.com" ||
      state.user.email === "genevieve.teetie@gmail.com" ||
      state.user.email === "regina@vitablehealth.com"
    )
  },
  allowShowAiFeatures: (state: any) => {
    return (
      (!!state && !!state.user && state.user.email === "dev@vitablehealth.com") ||
      state.user.email === "caitlin@vitablehealth.com" ||
      state.user.email === "np@np.com" ||
      state.user.email === "melanie@vitablehealth.com" ||
      state.user.email === "genevieve.teetie@gmail.com" ||
      state.user.email === "kate@vitablehealth.com"
    )
  },
  allowNewChartingUiFeatures: (_: any) => {
    return true
  },
  user: (state: any) => {
    return state.user
  },
  token: (state: any) => {
    return state.token
  },
  twoFactorPendingDetails: (state: any) => {
    return state.twoFactorPendingDetails
  },
  userId: (state: any) => {
    if (!state.user) {
      return undefined
    }
    return state.user.id
  },
  providerId: (state: any) => {
    if (!state.user || !state.user.provider || !!state.user.clinical_admin) {
      return undefined
    }
    return state.user.provider.id
  },
  provider: (state: any) => {
    if (!state.user || !state.user.provider || !!state.user.clinical_admin) {
      return undefined
    }
    return state.user.provider
  },
  clinicalAdmin: (state: any) => {
    if (!state.user || !state.user.clinical_admin) {
      return undefined
    }
    return state.user.clinical_admin
  },
  isProvider: (state: any) => {
    if (!state.user) {
      return false
    }
    return state.user.user_type === UserTypeEnum.NursePractitioner
  },
  isNursePractitioner: (state: any) => {
    if (!state.user) return false
    if (!state.user.provider) return false
    return state.user.provider.provider_type === ProviderTypeEnum.NursePractitioner
  },
  isPhlebotomist: (state: any) => {
    if (!state.user) return false
    if (!state.user.provider) return false
    return state.user.provider.provider_type === ProviderTypeEnum.Phlebotomist
  },
  isRegisteredNurse: (state: any) => {
    if (!state.user) return false
    if (!state.user.provider) return false
    return state.user.provider.provider_type === ProviderTypeEnum.RegisteredNurse
  },
  isClinicalAdmin: (state: any) => {
    if (!state.user) {
      return false
    }
    return !!state.user.clinical_admin
  },
  isTwoFactorPending: (state: any) => {
    if (!state.twoFactorPending) {
      return false
    }
    return state.twoFactorPending
  },
  isLoggedIn: (state: any) => {
    return state.user && state.token
  },
  profilePictureUrl: (state: any, getters: any) => {
    if (!getters.isLoggedIn) throw new Error("Cannot get profile picture url for current user, no user is logged in!")

    if (state.user.profile_picture_url && state.user.profile_picture_url.length) return state.user.profile_picture_url
    return "/imgs/doc-1.png"
  }
}

// actions
const actions = {
  async init({ dispatch }: any) {
    const storeCache = window.localStorage.getItem("vuex")

    // Do we have a store
    if (!storeCache) return await dispatch("reset")

    const store = JSON.parse(storeCache)

    const token = store.me.token
    const refreshToken = store.me.refreshToken

    if (token) asclepiusConfig.setJwtToken(store.me.token.split(" ")[1])

    if (!token || !refreshToken) return await dispatch("reset")

    // Is token valid
    await dispatch("refreshAccessToken")
  },

  async reset({ commit }: any) {
    commit("setUser", { token: undefined, user: undefined })
    commit("setTwoFactor", { pending: false })
    commit("setRefreshToken", { refreshToken: false })
  },

  async refreshAccessToken({ commit, state, dispatch }: any) {
    const { user, refreshToken, accessToken, refreshAccessToken, refreshAccessTokenResult } = useRefreshAccessToken()

    await refreshAccessToken(state.refreshToken)
    if (!refreshAccessTokenResult.value.isSuccess()) {
      return dispatch("logout")
    }

    commit("setUser", {
      token: `JWT ${accessToken.value}`,
      user: user.value
    })
    commit("setRefreshToken", { refreshToken: refreshToken.value })

    eventBus.$emit(globalEvents.LOG_IN)
  },

  async login({ commit }: any, payload: any) {
    const { user, token, refreshToken, postLoginRedirectPath, postLoginRedirectPageName } = payload

    // Update Store
    commit("setTwoFactor", { pending: false })
    commit("setUser", {
      token: `JWT ${token}`,
      user: user
    })
    commit("setRefreshToken", { refreshToken })

    // Post login redirection
    if (postLoginRedirectPath) {
      window.location.href = postLoginRedirectPath
    } else if (postLoginRedirectPageName) {
      router.push({ name: postLoginRedirectPageName })
    } else {
      router.push({ name: "appointments_calendar" })
    }

    // Emit event
    eventBus.$emit(globalEvents.LOG_IN)
  },

  async handleTwoFactor({ commit }: any, payload: any) {
    const { emailOrPhone, userType } = payload

    commit("setTwoFactor", {
      pending: true,
      emailOrPhone: emailOrPhone,
      userType: userType
    })

    // Redirection
    router.push({ name: "two-factor", query: { ...router.currentRoute.query } })
  },

  async logout({ dispatch }: any) {
    dispatch("reset")

    if (router.currentRoute.path !== "/login") {
      router.push({ name: "login" }).then()
    }
    eventBus.$emit(globalEvents.LOG_OUT)
  }
}

// mutations
const mutations = {
  setUser(state: any, { token, user }: any) {
    if (user?.clinical_admin) {
      user.provider = user.clinical_admin
    }
    state.token = token
    state.user = user

    if (token) asclepiusConfig.setJwtToken(token.split(" ")[1])
    else asclepiusConfig.clearJwtToken()
  },
  setTwoFactor(state: any, { pending, emailOrPhone = undefined, userType = undefined }: any) {
    state.twoFactorPending = pending
    state.twoFactorPendingDetails.emailOrPhone = emailOrPhone
    state.twoFactorPendingDetails.userType = userType
  },
  setRefreshToken(state: any, { refreshToken }: any) {
    state.refreshToken = refreshToken
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
